import {useEffect, useRef, useCallback} from 'react';

export const useGuruWalkTourDetector = (onTourDetected: (hasTours: boolean) => void) => {
  const observerRef = useRef<MutationObserver | null>(null);
  const lastStateRef = useRef<boolean>(false);

  const checkForTourCards = useCallback(() => {
    const component = document.querySelector('guruwalk-tour-cards');
    if (!component?.shadowRoot) return false;
    const tourCards = component.shadowRoot.querySelectorAll(
      '[class*="TourCard_tourCar"]',
    );
    return tourCards.length > 0;
  }, []);

  const handleMutation = useCallback(() => {
    const hasTourCards = checkForTourCards();

    if (hasTourCards !== lastStateRef.current) {
      lastStateRef.current = hasTourCards;
      onTourDetected(hasTourCards);
    }
  }, [checkForTourCards, onTourDetected]);

  useEffect(() => {
    const component = document.querySelector('guruwalk-tour-cards');
    if (!component) {
      return;
    }

    const observer = new MutationObserver(mutations => {
      let shouldCheck = false;

      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          shouldCheck = true;
        }
      });

      if (shouldCheck) {
        handleMutation();
      }
    });

    if (component.shadowRoot) {
      observer.observe(component.shadowRoot, {
        childList: true,
        subtree: true,
      });
    } else {
      const shadowObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (element.shadowRoot) {
                  observer.observe(element.shadowRoot, {
                    childList: true,
                    subtree: true,
                  });
                  shadowObserver.disconnect();
                }
              }
            });
          }
        });
      });

      shadowObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    observerRef.current = observer;

    handleMutation();

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [handleMutation]);
};
